import React, { useEffect, useState, useCallback } from 'react'
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CRow,
  CCol,
  CBadge,
  CSmartTable,
  CCollapse,
  CSpinner,
  CAlert,
  CFormCheck,
} from '@coreui/react-pro'
import { cilCheckAlt, cilX } from '@coreui/icons'
import CIcon from '@coreui/icons-react'
import CommunityPostsService from 'src/service/CommunityPostsService'

const AdminCommunityManager = () => {
  const [posts, setPosts] = useState(null)
  const [stats, setStats] = useState(null)
  const [selectedPosts, setSelectedPosts] = useState([])
  const [activePage, setActivePage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [columnFilter, setColumnFilter] = useState({})
  const [columnSorter, setColumnSorter] = useState({})
  const [loading, setLoading] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [alert, setAlert] = useState(null)
  const [filterPostType, setFilterPostType] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [details, setDetails] = useState([])

  const postTypes = [
    'winner',
    'round_winner',
    'round_top_teams',
    'player_of_round',
    'round_top_players',
    'ranking_update',
  ]

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await CommunityPostsService.getStats(filterPostType || undefined)
      if (response.status === 200) {
        setStats(response.data)
      }
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }, [filterPostType])

  // Fetch posts
  const fetchPosts = useCallback(async () => {
    setLoading(true)
    try {
      let paramsString = ''

      if (filterPostType) {
        paramsString += `post_type=${filterPostType}&`
      }

      if (searchTerm) {
        paramsString += `title=${encodeURIComponent(searchTerm)}&`
      }

      // Sorting
      if (columnSorter?.column) {
        paramsString += `sort_by=${columnSorter.column}&`
        paramsString += `sort_order=${columnSorter.state}&`
      }
      const offset = (Number(activePage) - 1) * Number(itemsPerPage)
      const pageNum = Number(activePage)

      const response = await CommunityPostsService.getCommunityPosts(
        filterPostType || '',
        offset,
        itemsPerPage,
        pageNum,
        paramsString,
      )

      if (response.status === 200) {
        setPosts(response.data)
      }
    } catch (error) {
      setAlert({
        color: 'danger',
        message: 'Failed to fetch posts',
      })
      console.error('Error fetching posts:', error)
    } finally {
      setLoading(false)
    }
  }, [activePage, itemsPerPage, filterPostType, searchTerm, columnSorter])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  useEffect(() => {
    fetchPosts()
  }, [fetchPosts])

  // Toggle post selection
  const togglePostSelection = (postId) => {
    setSelectedPosts((prev) =>
      prev.includes(postId) ? prev.filter((id) => id !== postId) : [...prev, postId],
    )
  }

  // Select/Deselect all
  const toggleSelectAll = () => {
    if (selectedPosts.length === posts?.data?.length) {
      setSelectedPosts([])
    } else {
      setSelectedPosts(posts?.data?.map((post) => post.id) || [])
    }
  }

  // Update single post status
  const handleSingleStatusUpdate = async (postId, newStatus) => {
    setUpdating(true)
    try {
      const response = await CommunityPostsService.updatePostStatus(postId, newStatus)
      if (response.status === 200) {
        setAlert({
          color: 'success',
          message: `Post ${newStatus ? 'activated' : 'deactivated'} successfully!`,
        })
        fetchPosts()
        fetchStats()
      }
    } catch (error) {
      setAlert({
        color: 'danger',
        message: 'Failed to update post status',
      })
    } finally {
      setUpdating(false)
    }
  }

  // Bulk update status
  const handleBulkStatusUpdate = async (newStatus) => {
    if (selectedPosts.length === 0) {
      setAlert({
        color: 'warning',
        message: 'Please select at least one post',
      })
      return
    }

    setUpdating(true)
    try {
      const response = await CommunityPostsService.bulkUpdateStatus(selectedPosts, newStatus)
      if (response.status === 200) {
        setAlert({
          color: 'success',
          message: `${response.data.updated_count} post(s) ${
            newStatus ? 'activated' : 'deactivated'
          } successfully!`,
        })
        setSelectedPosts([])
        fetchPosts()
        fetchStats()
      }
    } catch (error) {
      setAlert({
        color: 'danger',
        message: 'Failed to update posts',
      })
    } finally {
      setUpdating(false)
    }
  }

  const toggleDetails = (index) => {
    const position = details.indexOf(index)
    let newDetails = details.slice()
    if (position !== -1) {
      newDetails.splice(position, 1)
    } else {
      newDetails = [...details, index]
    }
    setDetails(newDetails)
  }

  const getPostTypeColor = (type) => {
    const colors = {
      winner: 'success',
      round_winner: 'info',
      round_top_teams: 'warning',
      player_of_round: 'danger',
      round_top_players: 'primary',
      ranking_update: 'secondary',
    }
    return colors[type] || 'secondary'
  }

  const columns = [
    {
      key: 'select',
      label: (
        <div className="d-flex justify-content-center align-items-center">
          <CFormCheck
            checked={selectedPosts.length === posts?.data?.length && posts?.data?.length > 0}
            onChange={toggleSelectAll}
            id="selectAll"
          />
        </div>
      ),
      filter: false,
      sorter: false,
      _style: {
        width: '2%',
        textAlign: 'center',
        verticalAlign: 'middle',
      },
    },
    {
      key: 'post_type',
      label: 'Post Type',
      filter: false,
      _style: { width: '12%' },
    },
    {
      key: 'title',
      label: 'Title / Description',
      filter: false,
      _style: { width: '25%' },
    },
    {
      key: 'source',
      label: 'Source',
      filter: false,
      _style: { width: '10%' },
    },
    {
      key: 'is_active',
      label: 'Status',
      filter: false,
      sorter: true,
      _style: { width: '12%' },
    },
    {
      key: 'created_at',
      label: 'Created',
      filter: false,
      _style: { width: '12%' },
    },
    {
      key: 'actions',
      label: 'Actions',
      filter: false,
      sorter: false,
      _style: { width: '12%' },
    },
  ]

  const scopedColumns = {
    select: (item) => (
      <td className="align-middle">
        <div className="d-flex justify-content-center align-items-center">
          <CFormCheck
            checked={selectedPosts.includes(item.id)}
            onChange={() => togglePostSelection(item.id)}
            id={`post-${item.id}`}
          />
        </div>
      </td>
    ),
    post_type: (item) => (
      <td>
        <CBadge color={getPostTypeColor(item.post_type)}>{item.post_type}</CBadge>
      </td>
    ),
    title: (item) => (
      <td>
        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', maxWidth: '300px' }}>
          <strong>{item.title || 'N/A'}</strong>
          <br />
          <small style={{ color: '#666' }}>{item.short_description || 'No description'}</small>
        </div>
      </td>
    ),
    source: (item) => (
      <td>
        <CBadge
          color={
            item.source === 'system' ? 'success' : item.source === 'admin' ? 'warning' : 'info'
          }
        >
          {item.source}
        </CBadge>
      </td>
    ),
    is_active: (item) => (
      <td>
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            id={`switch-${item.id}`}
            checked={item.is_active}
            onChange={(e) => handleSingleStatusUpdate(item.id, e.target.checked)}
            disabled={updating}
            style={{ cursor: updating ? 'not-allowed' : 'pointer' }}
          />
          <label className="form-check-label" htmlFor={`switch-${item.id}`}>
            {item.is_active ? (
              <CBadge color="success">Active</CBadge>
            ) : (
              <CBadge color="secondary">Inactive</CBadge>
            )}
          </label>
        </div>
      </td>
    ),
    created_at: (item) => <td>{new Date(item.created_at).toLocaleDateString()}</td>,
    actions: (item) => (
      <td>
        <CButton
          color="info"
          variant="outline"
          size="sm"
          onClick={() => toggleDetails(item.id)}
          className="me-2"
        >
          {details.includes(item.id) ? 'Hide' : 'View'}
        </CButton>
      </td>
    ),
    details: (item) => (
      <CCollapse visible={details.includes(item.id)}>
        <CCardBody className="bg-light p-4">
          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <strong>Post ID:</strong> {item.id}
              </div>
              <div className="mb-3">
                <strong>Post Type:</strong>{' '}
                <CBadge color={getPostTypeColor(item.post_type)}>{item.post_type}</CBadge>
              </div>
              <div className="mb-3">
                <strong>Source:</strong> <CBadge>{item.source}</CBadge>
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <strong>Status:</strong>{' '}
                {item.is_active ? (
                  <CBadge color="success">Active</CBadge>
                ) : (
                  <CBadge color="secondary">Inactive</CBadge>
                )}
              </div>
              <div className="mb-3">
                <strong>Created:</strong> {new Date(item.created_at).toLocaleString()}
              </div>
              <div className="mb-3">
                <strong>Updated:</strong> {new Date(item.updated_at).toLocaleString()}
              </div>
            </div>
          </div>
          <div className="border-top pt-3 mt-3">
            <strong>Payload:</strong>
            <pre
              style={{ fontSize: '12px', maxHeight: '200px', overflow: 'auto', marginTop: '10px' }}
            >
              {JSON.stringify(item.payload, null, 2)}
            </pre>
          </div>
        </CCardBody>
      </CCollapse>
    ),
  }

  return (
    <div>
      {/* Alert */}
      {alert && (
        <CAlert color={alert.color} className="mb-3" onClose={() => setAlert(null)} dismissible>
          {alert.message}
        </CAlert>
      )}

      {/* Stats Cards */}
      <CRow className="mb-4">
        <CCol xs={12} sm={6} md={4} className="mb-3">
          <CCard className="border-left-primary">
            <CCardBody>
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <div
                    style={{
                      fontSize: '12px',
                      color: '#858796',
                      textTransform: 'uppercase',
                      fontWeight: 'bold',
                    }}
                  >
                    Total Posts
                  </div>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#4e73df' }}>
                    {stats?.total_posts || 0}
                  </div>
                </div>
                <div style={{ fontSize: '40px', color: '#ddd' }}>📋</div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol xs={12} sm={6} md={4} className="mb-3">
          <CCard className="border-left-success">
            <CCardBody>
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <div
                    style={{
                      fontSize: '12px',
                      color: '#858796',
                      textTransform: 'uppercase',
                      fontWeight: 'bold',
                    }}
                  >
                    Active Posts
                  </div>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1cc88a' }}>
                    {stats?.active_posts || 0}
                  </div>
                </div>
                <div style={{ fontSize: '40px', color: '#ddd' }}>✓</div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol xs={12} sm={6} md={4} className="mb-3">
          <CCard className="border-left-danger">
            <CCardBody>
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <div
                    style={{
                      fontSize: '12px',
                      color: '#858796',
                      textTransform: 'uppercase',
                      fontWeight: 'bold',
                    }}
                  >
                    Inactive Posts
                  </div>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#e74a3b' }}>
                    {stats?.inactive_posts || 0}
                  </div>
                </div>
                <div style={{ fontSize: '40px', color: '#ddd' }}>✕</div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* Filters & Actions */}
      <CCard className="mb-4">
        <CCardHeader>
          <div className="d-flex justify-content-between align-items-center">
            <strong>Post Filters & Bulk Actions</strong>
            {selectedPosts.length > 0 && (
              <small className="text-muted">{selectedPosts.length} posts selected</small>
            )}
          </div>
        </CCardHeader>
        <CCardBody>
          <CRow className="mb-3">
            <CCol md={3}>
              <label className="mb-2">
                <small>Post Type</small>
              </label>
              <select
                className="form-control form-control-sm"
                value={filterPostType}
                onChange={(e) => {
                  setFilterPostType(e.target.value)
                  setActivePage(1)
                }}
              >
                <option value="">All Types</option>
                {postTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </CCol>

            {/* <CCol md={4}>
              <label className="mb-2">
                <small>Search Title</small>
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search by title..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  setActivePage(1)
                }}
              />
            </CCol> */}

            <CCol md={5} className="d-flex align-items-end gap-2">
              {selectedPosts.length > 0 && (
                <>
                  <CButton
                    color="success"
                    size="sm"
                    onClick={() => handleBulkStatusUpdate(true)}
                    disabled={updating}
                  >
                    {updating && <CSpinner component="span" size="sm" className="me-2" />}
                    <CIcon icon={cilCheckAlt} className="me-2" />
                    Activate {selectedPosts.length} Post(s)
                  </CButton>
                  <CButton
                    color="danger"
                    size="sm"
                    onClick={() => handleBulkStatusUpdate(false)}
                    disabled={updating}
                  >
                    {updating && <CSpinner component="span" size="sm" className="me-2" />}
                    <CIcon icon={cilX} className="me-2" />
                    Deactivate {selectedPosts.length} Post(s)
                  </CButton>
                </>
              )}
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      {/* Posts Table */}
      <CCard>
        <CCardHeader>
          <strong>Community Posts Management</strong>
        </CCardHeader>
        <CCardBody>
          {loading ? (
            <div className="text-center py-5">
              <CSpinner color="primary" />
              <p className="mt-3">Loading posts...</p>
            </div>
          ) : (
            <CSmartTable
              columns={columns}
              columnFilter={{ external: true }}
              columnSorter={{ external: true }}
              sorterValue={columnSorter}
              onSorterChange={(sorter) => {
                if (
                  columnSorter?.column === sorter?.column &&
                  columnSorter?.state === sorter?.state
                ) {
                  return
                }

                console.log('sorter changed:', sorter)

                setColumnSorter(sorter)
                setActivePage(1)
              }}
              scopedColumns={scopedColumns}
              items={posts?.data}
              itemsPerPage={itemsPerPage}
              itemsPerPageSelect
              loading={loading}
              pagination={{ external: true }}
              paginationProps={{
                activePage: activePage,
                pages: Math.ceil(posts?.total / itemsPerPage) || 1,
              }}
              tableProps={{
                hover: true,
                responsive: true,
                className: 'align-middle',
              }}
              onActivePageChange={(page) => setActivePage(Number(page) || 1)}
              onItemsPerPageChange={(items) => {
                setItemsPerPage(Number(items) || 10)
                setActivePage(1)
              }}
            />
          )}
        </CCardBody>
      </CCard>
    </div>
  )
}

export default AdminCommunityManager
